import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import { FaMapMarkerAlt, FaMoneyBillAlt, FaRupeeSign, FaDollarSign, FaDownload, FaArrowLeft, FaUser, FaPhone, FaEnvelope, FaHeadset, FaCheckCircle, FaTruck, FaClock, FaUtensils } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import rotiey from "../assets/images/roti-4.jpg";
import Config from '../API/Config';
import { getOrderDetail, cancelOrder, reorderItems } from '../API/OrderAPI';
import { PuffLoader } from 'react-spinners';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import '../styles/orderAnimations.css';

const OrderDetails = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);

    const CountryId = Config.getCountryId();
    const currencySymbol = CountryId === 'IN' ? <FaRupeeSign /> : <FaDollarSign />;

    useEffect(() => {
        fetchOrderDetail(true);

        // Set up interval to fetch order details every 15 seconds
        const interval = setInterval(() => {
            fetchOrderDetail(false);
        }, 30000);

        // Cleanup interval on component unmount
        return () => clearInterval(interval);
    }, [orderId]);

    const fetchOrderDetail = async (showLoader = true) => {
        if (!orderId) return;

        try {
            if (showLoader) setLoading(true);
            const response = await getOrderDetail(orderId);
            if (response?.responseData) {
                setOrderData(response.responseData);
            }
        } catch (error) {
            console.error('Error fetching order detail:', error);
            if (showLoader) toast.error('Failed to fetch order details');
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    const handleCancelOrder = async () => {
        const result = await Swal.fire({
            title: 'Cancel Order?',
            text: 'Are you sure you want to cancel this order? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Cancel Order',
            cancelButtonText: 'Keep Order'
        });

        if (result.isConfirmed) {
            try {
                await cancelOrder(orderData.Id);
                navigate('/my-order');
            } catch (error) {
                console.error('Error canceling order:', error);
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'text-orange-600';
            case 'CONFIRMED': return 'text-blue-600';
            case 'PREPARING': return 'text-yellow-600';
            case 'PICKED': return 'text-purple-600';
            case 'DELIVERED': return 'text-green-600';
            case 'CANCELED': return 'text-red-600';
            case 'REJECTED': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    const getStatusSteps = () => {
        const currentStatus = orderData?.OrderStatus;

        // For canceled/rejected orders, show simplified timeline
        if (currentStatus === 'CANCELED' || currentStatus === 'REJECTED') {
            return [
                { key: 'PENDING', label: 'Order Placed', icon: FaCheckCircle, completed: true, active: false, upcoming: false },
                { key: currentStatus, label: currentStatus === 'CANCELED' ? 'Order Canceled' : 'Order Rejected', icon: FaCheckCircle, completed: false, active: true, upcoming: false }
            ];
        }

        // Normal flow for other statuses
        const allSteps = [
            { key: 'PENDING', label: 'Order Placed', icon: FaCheckCircle },
            { key: 'CONFIRMED', label: 'Confirmed', icon: FaCheckCircle },
            { key: 'PREPARING', label: 'Preparing', icon: FaUtensils },
            { key: 'PICKED', label: 'On the Way', icon: FaTruck },
            { key: 'DELIVERED', label: 'Delivered', icon: FaCheckCircle }
        ];

        const currentIndex = allSteps.findIndex(step => step.key === currentStatus);

        return allSteps.map((step, index) => ({
            ...step,
            completed: index < currentIndex,
            active: step.key === currentStatus,
            upcoming: index > currentIndex
        }));
    };

    const getStatusMessage = () => {
        switch (orderData?.OrderStatus) {
            case 'PENDING': return 'Your order has been placed successfully!';
            case 'CONFIRMED': return 'Your order has been confirmed and is being prepared.';
            case 'PREPARING': return 'Your delicious meal is being prepared with care.';
            case 'PICKED': return 'Your order is on the way! Our delivery partner will reach you soon.';
            case 'DELIVERED': return 'Your order has been delivered successfully!';
            case 'CANCELED': return 'Your order has been canceled.';
            case 'REJECTED': return 'Your order has been rejected.';
            default: return 'Order status updated.';
        }
    };

    if (loading) {
        return (
            <>
                <Breadcrumb title="Order Details" path="Order Details" />
                <div className="bg-[#f5f5f5]">
                    <div className="container mx-auto px-4 py-10">
                        {/* Back Button Placeholder */}
                        <div className="h-6 w-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-6 animate-pulse"></div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left Column Placeholders */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Order Status Placeholder */}
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 animate-pulse">
                                    <div className="h-6 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-4"></div>
                                    <div className="h-4 w-64 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-6"></div>

                                    {/* Desktop Timeline Placeholder */}
                                    <div className="hidden sm:flex justify-between items-center mb-6">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="flex flex-col items-center">
                                                <div className="w-12 h-12 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full mb-3"></div>
                                                <div className="h-3 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Mobile Timeline Placeholder */}
                                    <div className="sm:hidden space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="flex items-center space-x-4">
                                                <div className="w-10 h-10 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full"></div>
                                                <div className="h-4 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Order Header Placeholder */}
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 animate-pulse">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="h-8 w-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                    </div>
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-16 h-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full"></div>
                                        <div className="space-y-2">
                                            <div className="h-5 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                            <div className="h-4 w-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                            <div className="h-4 w-36 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="border rounded-lg p-4 bg-gray-50">
                                            <div className="h-4 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-2"></div>
                                            <div className="h-5 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                        </div>
                                        <div className="border rounded-lg p-4 bg-gray-50">
                                            <div className="h-4 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-2"></div>
                                            <div className="h-5 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Placeholder */}
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 animate-pulse">
                                    <div className="h-6 w-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-4"></div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="h-4 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-2"></div>
                                        <div className="h-4 w-3/4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-6">
                                {/* Order Summary Placeholder */}
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 h-fit animate-pulse">
                                    <div className="h-6 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-4"></div>

                                    {/* Items Placeholder */}
                                    <div className="space-y-3 mb-6">
                                        {[...Array(2)].map((_, i) => (
                                            <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <div className="h-4 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                                <div className="h-4 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Bill Details Placeholder */}
                                    <div className="space-y-2 border-t pt-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="flex justify-between">
                                                <div className="h-4 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                                <div className="h-4 w-12 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Buttons Placeholder */}
                                    <div className="mt-6 space-y-3">
                                        <div className="h-12 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full"></div>
                                    </div>
                                </div>

                                {/* Support Section Placeholder */}
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 animate-pulse">
                                    <div className="h-6 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-4"></div>
                                    <div className="bg-gray-50 p-4 rounded-lg border">
                                        <div className="h-4 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded mb-2"></div>
                                        <div className="h-4 w-2/3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumb title="Order Details" path="Order Details" />

            <div className="bg-[#f5f5f5] ">
                <div className="container mx-auto px-4 py-10">
                    {/* Back Button */}
                    <Link
                        to="/my-order"
                        className="inline-flex items-center text-theme2 hover:text-black mb-6 font-medium transition-colors duration-200"
                    >
                        <FaArrowLeft className="mr-2" />
                        Back to My Orders
                    </Link>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Order Information */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Order Status Tracking */}
                            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 animate-fadeIn">
                                <h3 className="text-lg font-semibold mb-2 text-darktext">Order Status</h3>
                                <p className="text-theme2 font-medium mb-6 animate-slideInUp">{getStatusMessage()}</p>

                                {/* Mobile-first responsive design */}
                                <div className="relative">
                                    {/* Progress Line - Hidden on mobile, visible on larger screens */}
                                    <div className="hidden sm:block absolute top-6 left-6 right-6 h-0.5 bg-gray-200">
                                        <div
                                            className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-1000 ease-in-out animate-progressFill"
                                            style={{ width: `${(getStatusSteps().filter(s => s.completed).length / (getStatusSteps().length - 1)) * 100}%` }}
                                        ></div>
                                    </div>

                                    {/* Desktop Layout */}
                                    <div className="hidden sm:flex justify-between items-start relative z-10">
                                        {getStatusSteps().map((step, index) => {
                                            const IconComponent = step.icon;
                                            return (
                                                <div key={step.key} className="flex flex-col items-center animate-fadeInScale" style={{ animationDelay: `${index * 200}ms` }}>
                                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 transition-all duration-500 transform hover:scale-110 ${step.completed
                                                        ? 'bg-green-500 border-green-500 text-white shadow-lg animate-checkmark'
                                                        : step.active
                                                            ? step.key === 'DELIVERED'
                                                                ? 'bg-green-500 border-green-500 text-white shadow-lg animate-checkmark'
                                                                : step.key === 'CANCELED' || step.key === 'REJECTED'
                                                                    ? 'bg-red-500 border-red-500 text-white shadow-lg'
                                                                    : 'bg-blue-500 border-blue-500 text-white shadow-lg animate-pulse-glow'
                                                            : 'bg-white border-gray-300 text-gray-400'
                                                        }`}>
                                                        {step.active && step.key === 'PICKED' ? (
                                                            <FaTruck className="text-lg animate-wiggle" />
                                                        ) : (
                                                            <IconComponent className={`text-lg ${step.active && step.key !== 'DELIVERED' && step.key !== 'CANCELED' && step.key !== 'REJECTED' ? 'animate-bounce' : ''}`} />
                                                        )}
                                                    </div>
                                                    <span className={`text-xs mt-3 font-medium text-center max-w-[80px] leading-tight transition-colors duration-300 ${step.completed || step.active ? 'text-gray-800' : 'text-gray-500'
                                                        }`}>
                                                        {step.label}
                                                    </span>
                                                    {step.active && step.key !== 'CANCELED' && step.key !== 'REJECTED' && step.key !== 'DELIVERED' && (
                                                        <div className="mt-2 animate-fadeIn">
                                                            <div className="flex space-x-1">
                                                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Mobile Layout - Vertical Timeline */}
                                    <div className="sm:hidden space-y-4">
                                        {getStatusSteps().map((step, index) => {
                                            const IconComponent = step.icon;
                                            return (
                                                <div key={step.key} className="flex items-center space-x-4 animate-slideInLeft" style={{ animationDelay: `${index * 150}ms` }}>
                                                    <div className="relative">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-3 transition-all duration-500 ${step.completed
                                                            ? 'bg-green-500 border-green-500 text-white shadow-md animate-checkmark'
                                                            : step.active
                                                                ? step.key === 'DELIVERED'
                                                                    ? 'bg-green-500 border-green-500 text-white shadow-md animate-checkmark'
                                                                    : step.key === 'CANCELED' || step.key === 'REJECTED'
                                                                        ? 'bg-red-500 border-red-500 text-white shadow-md'
                                                                        : 'bg-blue-500 border-blue-500 text-white shadow-md animate-pulse-glow'
                                                                : 'bg-white border-gray-300 text-gray-400'
                                                            }`}>
                                                            {step.active && step.key === 'PICKED' ? (
                                                                <FaTruck className="text-sm animate-wiggle" />
                                                            ) : (
                                                                <IconComponent className={`text-sm ${step.active && step.key !== 'DELIVERED' && step.key !== 'CANCELED' && step.key !== 'REJECTED' ? 'animate-bounce' : ''}`} />
                                                            )}
                                                        </div>
                                                        {index < getStatusSteps().length - 1 && (
                                                            <div className={`absolute top-10 left-1/2 transform -translate-x-1/2 w-0.5 h-8 transition-colors duration-500 ${step.completed ? 'bg-green-500' : 'bg-gray-200'
                                                                }`}></div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <span className={`text-sm font-medium transition-colors duration-300 ${step.completed || step.active ? 'text-gray-800' : 'text-gray-500'
                                                            }`}>
                                                            {step.label}
                                                        </span>
                                                        {step.active && step.key !== 'CANCELED' && step.key !== 'REJECTED' && step.key !== 'DELIVERED' && (
                                                            <div className="flex space-x-1 mt-1">
                                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Enhanced Special Animation for All Status */}
                                {orderData?.OrderStatus === 'PENDING' && (
                                    <div className="mt-6 bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaCheckCircle className="text-2xl text-orange-600 animate-bounce" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-ping"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-orange-700 font-semibold text-base sm:text-lg animate-pulse">Order Placed Successfully!</p>
                                                <p className="text-orange-600 text-sm">We've received your order and processing it</p>
                                            </div>
                                            <div className="text-2xl animate-bounce">📋</div>
                                        </div>
                                    </div>
                                )}

                                {orderData?.OrderStatus === 'CONFIRMED' && (
                                    <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaCheckCircle className="text-2xl text-blue-600 animate-pulse" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-ping"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-blue-700 font-semibold text-base sm:text-lg">Order Confirmed!</p>
                                                <p className="text-blue-600 text-sm">Your order has been confirmed and will be prepared soon</p>
                                            </div>
                                            <div className="text-2xl animate-bounce">✅</div>
                                        </div>
                                    </div>
                                )}
                                {orderData?.OrderStatus === 'PICKED' && (
                                    <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaTruck className="text-2xl text-blue-600 animate-wiggle" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                                                <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-blue-700 font-semibold text-base sm:text-lg animate-pulse">Your order is on the way!</p>
                                                <p className="text-blue-600 text-sm">Our delivery partner will reach you soon</p>
                                            </div>
                                            <div className="flex space-x-1">
                                                <div className="w-2 h-6 sm:h-8 bg-blue-400 rounded animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                                <div className="w-2 h-4 sm:h-6 bg-blue-500 rounded animate-pulse" style={{ animationDelay: '200ms' }}></div>
                                                <div className="w-2 h-3 sm:h-4 bg-blue-600 rounded animate-pulse" style={{ animationDelay: '400ms' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Preparing Animation */}
                                {orderData?.OrderStatus === 'PREPARING' && (
                                    <div className="mt-6 bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border border-yellow-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaUtensils className="text-2xl text-yellow-600" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-ping"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-yellow-700 font-semibold text-base sm:text-lg">Your meal is being prepared!</p>
                                                <p className="text-yellow-600 text-sm">Our chefs are working on your delicious order</p>
                                            </div>
                                            <div className="flex space-x-1">
                                                <div className="w-2 h-6 sm:h-8 bg-yellow-400 rounded animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                                <div className="w-2 h-4 sm:h-6 bg-orange-500 rounded animate-pulse" style={{ animationDelay: '200ms' }}></div>
                                                <div className="w-2 h-3 sm:h-4 bg-red-500 rounded animate-pulse" style={{ animationDelay: '400ms' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Delivered Animation */}
                                {orderData?.OrderStatus === 'DELIVERED' && (
                                    <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaCheckCircle className="text-2xl text-green-600 animate-bounce" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-green-700 font-semibold text-base sm:text-lg">Order Delivered Successfully!</p>
                                                <p className="text-green-600 text-sm">Thank you for choosing us. Enjoy your meal!</p>
                                            </div>
                                            <div className="text-2xl animate-bounce">🎉</div>
                                        </div>
                                    </div>
                                )}

                                {/* Canceled Animation */}
                                {orderData?.OrderStatus === 'CANCELED' && (
                                    <div className="mt-6 bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaCheckCircle className="text-2xl text-red-600 animate-pulse" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-red-700 font-semibold text-base sm:text-lg">Order Canceled</p>
                                                <p className="text-red-600 text-sm">Your order has been canceled successfully</p>
                                            </div>
                                            <div className="text-2xl animate-bounce">❌</div>
                                        </div>
                                    </div>
                                )}

                                {/* Rejected Animation */}
                                {orderData?.OrderStatus === 'REJECTED' && (
                                    <div className="mt-6 bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200 animate-slideInUp">
                                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-3">
                                            <div className="relative">
                                                <FaCheckCircle className="text-2xl text-red-600 animate-pulse" />
                                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                                            </div>
                                            <div className="text-center sm:text-left">
                                                <p className="text-red-700 font-semibold text-base sm:text-lg">Order Rejected</p>
                                                <p className="text-red-600 text-sm">Unfortunately, your order has been rejected</p>
                                            </div>
                                            <div className="text-2xl animate-bounce">⚠️</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Order Header */}
                            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                                <h2 className="text-xl sm:text-2xl font-semibold text-darktext mb-4">Order Details</h2>

                                <div className="flex flex-col gap-3 mb-4">
                                    {orderData?.Items?.map((item, i) => (
                                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 border">
                                            <img
                                                src={item.ProductThumbImage || 'https://via.placeholder.com/48'}
                                                alt={item.ProductName}
                                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-200"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-sm text-darktext truncate">{item.ProductName}</p>
                                                <p className="text-xs text-contentcolor">{item.VariationName} · Pack of {item.PackSize}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <p className="text-xs text-contentcolor">x{item.Qty}</p>
                                                <p className="text-sm font-semibold text-theme2 flex items-center justify-end gap-0.5">{currencySymbol}{parseFloat(item.Price).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-4">
                                    <p className="text-contentcolor text-sm">Order No: <span className="font-medium text-darktext">{orderData?.OrderNo}</span></p>
                                    <p className="text-contentcolor text-xs sm:text-sm">
                                        {new Date(orderData?.EntDt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        {' at '}
                                        {new Date(orderData?.EntDt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <p className="text-xs text-contentcolor mb-1">Order Status</p>
                                        <p className={`font-semibold text-sm ${getStatusColor(orderData?.OrderStatus)}`}>{orderData?.OrderStatus}</p>
                                    </div>
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <p className="text-xs text-contentcolor mb-1">Payment Status</p>
                                        <p className={`font-semibold text-sm ${orderData?.PaymentStatus === 'PAID' ? 'text-green-600' : 'text-red-600'}`}>{orderData?.PaymentStatus}</p>
                                    </div>
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <p className="text-xs text-contentcolor mb-1">Payment Date</p>
                                        <p className="font-medium text-sm text-darktext">
                                            {orderData?.PaymentDt ? new Date(orderData.PaymentDt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                        </p>
                                    </div>
                                    <div className="border rounded-lg p-3 bg-gray-50">
                                        <p className="text-xs text-contentcolor mb-1">Transaction ID</p>
                                        <p className="font-medium text-sm text-darktext break-all">{orderData?.TransactionId || '—'}</p>
                                    </div>
                                </div>
                            </div>

                            

                            {/* Delivery Address */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="text-lg font-semibold mb-4 flex items-center">
                                    <FaMapMarkerAlt className="text-theme2 mr-2" />
                                    Delivery Address
                                </h3>
                                <div className="flex items-start bg-gray-50 p-4 rounded-lg border">
                                    <FaLocationDot className="text-theme2 mt-1 mr-3 flex-shrink-0" />
                                    <div>
                                        <p className="text-contentcolor leading-relaxed">{orderData?.Address}</p>
                                        <p className="text-contentcolor text-sm mt-1">{orderData?.State}, {orderData?.Country}</p>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className='space-y-6'>
                            {/* Order Summary */}
                            <div className="bg-white rounded-lg shadow-md p-6 h-fit">
                                <h3 className="text-lg font-semibold mb-4 text-darktext">Order Summary</h3>

                                {/* Order Items */}
                                <div className="space-y-3 mb-3">
                                    {orderData?.Items?.map((item, index) => (
                                        <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                            <div>
                                                <span className="font-medium">{item.ProductName}</span>
                                                <span className="text-contentcolor ml-2">x{item.Qty}</span>
                                                <p className="text-xs text-contentcolor">{item.VariationName} ({item.PackSize})</p>
                                            </div>
                                            <div className="flex items-center text-theme2 font-semibold">
                                                {currencySymbol}
                                                <span className="ml-1">{parseFloat(item.Price).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Bill Details */}
                                <div className="space-y-2 border-t pt-4 text-sm">
                                    <div className="flex justify-between text-contentcolor">
                                        <span>Subtotal</span>
                                        <span className="flex items-center gap-0.5">{currencySymbol}{parseFloat(orderData?.SubTotal || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-contentcolor">
                                        <span>GST</span>
                                        <span className="flex items-center gap-0.5">{currencySymbol}{parseFloat(orderData?.TaxAmount || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-contentcolor">
                                        <span>Delivery Charge</span>
                                        <span className={parseFloat(orderData?.DeliveryCharge || 0) === 0 ? 'text-green-600 font-medium' : 'flex items-center gap-0.5'}>
                                            {parseFloat(orderData?.DeliveryCharge || 0) === 0 ? 'Free' : <>{currencySymbol}{parseFloat(orderData?.DeliveryCharge || 0).toFixed(2)}</>}
                                        </span>
                                    </div>
                                    {parseFloat(orderData?.ServiceCharge || 0) > 0 && (
                                        <div className="flex justify-between text-contentcolor">
                                            {/* <span>Service Charge {parseFloat(orderData?.ServiceChargeTax || 0) > 0 && <span className="text-xs">({orderData.ServiceChargeTax}% tax incl.)</span>}</span> */}
                                            <span>Service Charge</span>
                                            <span className="flex items-center gap-0.5">{currencySymbol}{parseFloat(orderData?.ServiceCharge || 0).toFixed(2)}</span>
                                        </div>
                                    )}
                                    {parseFloat(orderData?.DiscountAmount || 0) > 0 && (
                                        <div className="flex justify-between text-contentcolor">
                                            <span>Discount</span>
                                            <span className="text-green-600 flex items-center gap-0.5">-{currencySymbol}{parseFloat(orderData?.DiscountAmount || 0).toFixed(2)}</span>
                                        </div>
                                    )}
                                    {orderData?.CouponCode && parseFloat(orderData?.CouponAmount || 0) > 0 && (
                                        <div className="flex justify-between text-contentcolor">
                                            <span>Coupon <span className="font-medium text-theme2">({orderData.CouponCode})</span></span>
                                            <span className="text-green-600 flex items-center gap-0.5">-{currencySymbol}{parseFloat(orderData?.CouponAmount || 0).toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="border-t pt-3 mt-3">
                                        <div className="flex justify-between text-lg font-bold text-theme2">
                                            <span>Total Paid</span>
                                            <span className="flex items-center gap-0.5">{currencySymbol}{parseFloat(orderData?.Total || 0).toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                {orderData?.OrderStatus === 'PENDING' && (
                                    <div className="mt-6 space-y-3">
                                        <button
                                            onClick={handleCancelOrder}
                                            className="w-full bg-red-600 text-white py-3 px-4 rounded-full hover:bg-red-700 transition-colors duration-300 font-semibold"
                                        >
                                            Cancel Order
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* Seller Section */}
                            {orderData?.Seller && (
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <FaUser className="text-theme2 mr-2" />
                                        Seller
                                    </h3>
                                    <div className="flex items-center bg-gray-50 p-4 rounded-lg border gap-4">
                                        <div className="w-12 h-12 bg-theme2 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                            {orderData.Seller.Name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-base">{orderData.Seller.Name}</p>
                                            <div className="flex items-center text-contentcolor text-sm mt-1">
                                                <FaPhone className="mr-2 flex-shrink-0" />
                                                <span>{orderData.Seller.CCODE} {orderData.Seller.Phone}</span>
                                            </div>
                                            {orderData.Seller.Email && (
                                                <div className="flex items-center text-contentcolor text-sm mt-1">
                                                    <FaEnvelope className="mr-2 flex-shrink-0" />
                                                    <span className="break-all">{orderData.Seller.Email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Delivery Boy Section */}
                            {orderData?.DeliveryBoy && (
                                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <FaTruck className="text-theme2 mr-2" />
                                        Delivery Partner
                                    </h3>
                                    <div className="flex items-center bg-gray-50 p-4 rounded-lg border gap-4">
                                        <div className="w-12 h-12 bg-theme2 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                            {orderData.DeliveryBoy.Name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-base">{orderData.DeliveryBoy.Name}</p>
                                            <div className="flex items-center text-contentcolor text-sm mt-1">
                                                <FaPhone className="mr-2 flex-shrink-0" />
                                                <span>{orderData.DeliveryBoy.CCODE} {orderData.DeliveryBoy.Phone}</span>
                                            </div>
                                            {orderData.DeliveryBoy.Email && (
                                                <div className="flex items-center text-contentcolor text-sm mt-1">
                                                    <FaEnvelope className="mr-2 flex-shrink-0" />
                                                    <span className="break-all">{orderData.DeliveryBoy.Email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Support Section */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h3 className="text-lg font-semibold mb-4 flex items-center">
                                    <FaHeadset className="text-theme2 mr-2" />
                                    Need Help?
                                </h3>
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <p className="text-contentcolor mb-3">For any queries or support regarding your order, feel free to contact us:</p>
                                    <div className="flex items-center text-theme2 font-semibold">
                                        <FaEnvelope className="mr-2" />
                                        <a href="mailto:customer@rotiey.com" className="hover:underline">
                                            customer@rotiey.com
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OrderDetails;